/**
 * Vuex Module for Sales Dashboard
 * Provides state management for the Sales Dashboard with 5-minute caching
 */

import axios from 'axios';

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Generate a cache key from filter parameters
 */
function generateCacheKey(filters, additionalParams = {}) {
  const keyParts = {
    sellerId: filters.sellerId || '',
    year: filters.year || '',
    quarter: filters.quarter || '',
    month: filters.month || '',
    category: filters.category || '',
    contractType: filters.contractType || 'all',
    startDate: filters.startDate || '',
    endDate: filters.endDate || '',
    dateFilterType: filters.dateFilterType || 'contract_start',
    ...additionalParams
  };
  return JSON.stringify(keyParts);
}

const state = {
  // Dashboard data caches (keyed by filter combination)
  cache: {
    kpis: {},           // { cacheKey: { data, timestamp } }
    monthlyRevenue: {}, 
    productStats: {},
    salespersonStats: {},
    contracts: {},
    velocity: {},
    products: { data: null, timestamp: null } // Products don't change with filters
  },
  
  // Sellers cache (doesn't change with filters)
  sellers: [],
  sellersTimestamp: null,
  
  // Current active data (for components to use)
  currentData: {
    kpis: {
      mrr: 0,
      arr: 0,
      acv: 0,
      newSignedContractsCount: 0,
      newMrr: 0,
      activeContracts: 0,
      totalContracts: 0,
      churnRate: 0,
      churnedCount: 0,
      periodChurnedCount: 0,
      avgMarginPercent: 0,
      avgContractValue: 0,
      avgCashSale: 0,
      totalRevenue: 0,
      cashSalesCount: 0,
      subscriptionCount: 0,
      avgContractLengthMonths: 0,
      avgProductsPerContract: 0,
      avgRentalPricePerProduct: 0,
      avgCashPricePerProduct: 0,
      totalCashRevenue: 0,
      totalSubscriptionRevenue: 0,
      totalProductsSold: 0,
      marginCalculableProducts: 0,
      soldAlarmsCount: 0,
      soldAjaxCount: 0,
      avgAlarmPrice: 0,
      totalSetupFees: 0
    },
    monthlyRevenue: [],
    productStats: { products: [], byCategory: [], coverage: null },
    salespersons: [],
    contracts: [],
    velocity: null,
    products: []
  },
  
  // Filters
  filters: {
    sellerId: '',
    year: new Date().getFullYear().toString(),
    quarter: '',
    month: '',
    category: 'rental', // Default to rental agreements
    contractType: 'all',
    startDate: '',
    endDate: '',
    dateFilterType: 'contract_start', // 'contract_start' for ARR/MRR, 'pdf_upload_date' for ACV
    onlyNewData: false // Only include agreements created after 2026-01-27
  },
  selectedYear: new Date().getFullYear(),
  
  // Loading states
  isLoading: false,
  contractsLoading: false,
  error: null
};

const getters = {
  // Current data getters
  kpis: (state) => state.currentData.kpis,
  monthlyRevenue: (state) => state.currentData.monthlyRevenue,
  // Return full object with products, byCategory, and coverage
  productStats: (state) => {
    const stats = state.currentData.productStats;
    // Handle both old format (array) and new format (object with products array)
    if (Array.isArray(stats)) {
      return { products: stats, byCategory: [], coverage: null };
    }
    return stats || { products: [], byCategory: [], coverage: null };
  },
  salespersons: (state) => state.currentData.salespersons,
  contracts: (state) => state.currentData.contracts,
  velocity: (state) => state.currentData.velocity,
  products: (state) => state.currentData.products,
  sellers: (state) => state.sellers,
  filters: (state) => state.filters,
  selectedYear: (state) => state.selectedYear,
  isLoading: (state) => state.isLoading,
  contractsLoading: (state) => state.contractsLoading,
  error: (state) => state.error,
  
  // Check if cache is fresh for a specific data type and filter combination
  isCacheFresh: (state) => (dataType, cacheKey) => {
    const cacheEntry = state.cache[dataType]?.[cacheKey];
    if (!cacheEntry || !cacheEntry.timestamp) return false;
    const age = Date.now() - cacheEntry.timestamp;
    return age < CACHE_DURATION;
  },
  
  // Get cache age in seconds
  cacheAge: (state) => (dataType, cacheKey) => {
    const cacheEntry = state.cache[dataType]?.[cacheKey];
    if (!cacheEntry || !cacheEntry.timestamp) return null;
    return Math.floor((Date.now() - cacheEntry.timestamp) / 1000);
  },
  
  // Build filter params for API calls
  filterParams: (state) => {
    const params = {};
    
    if (state.filters.sellerId) {
      params.seller_id = state.filters.sellerId;
    }
    
    if (state.filters.startDate) {
      params.start_date = state.filters.startDate;
    } else if (state.filters.year) {
      const year = parseInt(state.filters.year);
      let startMonth = 0;
      let endMonth = 11;
      
      if (state.filters.quarter) {
        const quarter = parseInt(state.filters.quarter);
        startMonth = (quarter - 1) * 3;
        endMonth = startMonth + 2;
      }
      
      if (state.filters.month) {
        startMonth = parseInt(state.filters.month) - 1;
        endMonth = startMonth;
      }
      
      params.start_date = new Date(year, startMonth, 1).toISOString().slice(0, 10);
      params.end_date = new Date(year, endMonth + 1, 0).toISOString().slice(0, 10);
    }
    
    if (state.filters.endDate) {
      params.end_date = state.filters.endDate;
    }
    
    if (state.filters.category) {
      params.category = state.filters.category;
    }
    
    if (state.filters.contractType && state.filters.contractType !== 'all') {
      params.contract_type = state.filters.contractType;
    }
    
    // Add date filter type for ACV vs ARR/MRR differentiation
    if (state.filters.dateFilterType) {
      params.date_filter_type = state.filters.dateFilterType;
    }
    
    // Add onlyNewData filter (only agreements created after 2026-01-27)
    if (state.filters.onlyNewData) {
      params.only_new_data = 'true';
    }
    
    return params;
  },
  
  // Get current cache key based on active filters
  currentCacheKey: (state) => {
    return generateCacheKey(state.filters);
  },
  
  currentCacheKeyWithYear: (state) => {
    return generateCacheKey(state.filters, { selectedYear: state.selectedYear });
  }
};

const mutations = {
  SET_CACHE_DATA(state, { dataType, cacheKey, data }) {
    if (!state.cache[dataType]) {
      state.cache[dataType] = {};
    }
    state.cache[dataType][cacheKey] = {
      data,
      timestamp: Date.now()
    };
  },
  
  SET_CURRENT_KPIS(state, kpis) {
    state.currentData.kpis = { ...state.currentData.kpis, ...kpis };
  },
  
  SET_CURRENT_MONTHLY_REVENUE(state, data) {
    state.currentData.monthlyRevenue = data;
  },
  
  SET_CURRENT_PRODUCT_STATS(state, data) {
    state.currentData.productStats = data;
  },
  
  SET_CURRENT_SALESPERSONS(state, data) {
    state.currentData.salespersons = data;
  },
  
  SET_CURRENT_CONTRACTS(state, data) {
    state.currentData.contracts = data;
  },
  
  SET_CURRENT_VELOCITY(state, data) {
    state.currentData.velocity = data;
  },
  
  SET_CURRENT_PRODUCTS(state, data) {
    state.currentData.products = data;
  },
  
  SET_PRODUCTS_CACHE(state, { data, timestamp }) {
    state.cache.products = { data, timestamp };
  },
  
  SET_SELLERS(state, data) {
    state.sellers = data;
    state.sellersTimestamp = Date.now();
  },
  
  SET_FILTERS(state, filters) {
    state.filters = { ...state.filters, ...filters };
  },
  
  RESET_FILTERS(state) {
    state.filters = {
      sellerId: '',
      year: new Date().getFullYear().toString(),
      quarter: '',
      month: '',
      category: 'rental', // Default to rental agreements
      contractType: 'all',
      startDate: '',
      endDate: ''
    };
  },
  
  SET_SELECTED_YEAR(state, year) {
    state.selectedYear = year;
  },
  
  SET_LOADING(state, isLoading) {
    state.isLoading = isLoading;
  },
  
  SET_CONTRACTS_LOADING(state, isLoading) {
    state.contractsLoading = isLoading;
  },
  
  SET_ERROR(state, error) {
    state.error = error;
  },
  
  CLEAR_ALL_CACHE(state) {
    state.cache = {
      kpis: {},
      monthlyRevenue: {},
      productStats: {},
      salespersonStats: {},
      contracts: {},
      velocity: {},
      products: { data: null, timestamp: null }
    };
  }
};

const actions = {
  /**
   * Fetch KPIs with caching
   */
  async fetchKPIs({ commit, state, getters }, { force = false } = {}) {
    const cacheKey = getters.currentCacheKey;
    
    // Check cache
    if (!force && getters.isCacheFresh('kpis', cacheKey)) {
      const cachedData = state.cache.kpis[cacheKey].data;
      commit('SET_CURRENT_KPIS', cachedData);
      return { fromCache: true, data: cachedData };
    }
    
    try {
      const params = new URLSearchParams(getters.filterParams);
      const response = await axios.get(
        `${process.env.VUE_APP_SERVER_URL}/sales/dashboard/overview?${params}`
      );
      const data = response.data.data;
      
      commit('SET_CACHE_DATA', { dataType: 'kpis', cacheKey, data });
      commit('SET_CURRENT_KPIS', data);
      
      return { fromCache: false, data };
    } catch (error) {
      console.error('Error fetching KPIs:', error);
      commit('SET_ERROR', error.message);
      throw error;
    }
  },
  
  /**
   * Fetch monthly revenue with caching
   */
  async fetchMonthlyRevenue({ commit, state, getters }, { force = false } = {}) {
    const cacheKey = getters.currentCacheKeyWithYear;
    
    if (!force && getters.isCacheFresh('monthlyRevenue', cacheKey)) {
      const cachedData = state.cache.monthlyRevenue[cacheKey].data;
      commit('SET_CURRENT_MONTHLY_REVENUE', cachedData);
      return { fromCache: true, data: cachedData };
    }
    
    try {
      const params = new URLSearchParams({
        year: state.selectedYear,
        ...getters.filterParams
      });
      const response = await axios.get(
        `${process.env.VUE_APP_SERVER_URL}/sales/dashboard/revenue?${params}`
      );
      const data = response.data.data || [];
      
      commit('SET_CACHE_DATA', { dataType: 'monthlyRevenue', cacheKey, data });
      commit('SET_CURRENT_MONTHLY_REVENUE', data);
      
      return { fromCache: false, data };
    } catch (error) {
      console.error('Error fetching monthly revenue:', error);
      commit('SET_ERROR', error.message);
      throw error;
    }
  },
  
  /**
   * Fetch product stats with caching
   */
  async fetchProductStats({ commit, state, getters }, { force = false } = {}) {
    const cacheKey = getters.currentCacheKey;
    
    if (!force && getters.isCacheFresh('productStats', cacheKey)) {
      const cachedData = state.cache.productStats[cacheKey].data;
      commit('SET_CURRENT_PRODUCT_STATS', cachedData);
      return { fromCache: true, data: cachedData };
    }
    
    try {
      const params = new URLSearchParams(getters.filterParams);
      const response = await axios.get(
        `${process.env.VUE_APP_SERVER_URL}/sales/dashboard/products?${params}`
      );
      
      // Backend returns { products: [], byCategory: [], coverage: {} } - keep full object
      const responseData = response.data.data || {};
      // Store the full object so components can access products, byCategory, AND coverage
      const data = responseData;
      
      commit('SET_CACHE_DATA', { dataType: 'productStats', cacheKey, data });
      commit('SET_CURRENT_PRODUCT_STATS', data);
      
      return { fromCache: false, data };
    } catch (error) {
      console.error('Error fetching product stats:', error);
      commit('SET_ERROR', error.message);
      throw error;
    }
  },
  
  /**
   * Fetch salesperson stats with caching
   */
  async fetchSalespersonStats({ commit, state, getters }, { force = false } = {}) {
    const cacheKey = getters.currentCacheKeyWithYear;
    
    if (!force && getters.isCacheFresh('salespersonStats', cacheKey)) {
      const cachedData = state.cache.salespersonStats[cacheKey].data;
      commit('SET_CURRENT_SALESPERSONS', cachedData);
      return { fromCache: true, data: cachedData };
    }
    
    try {
      const params = new URLSearchParams({
        year: state.selectedYear,
        ...getters.filterParams
      });
      const response = await axios.get(
        `${process.env.VUE_APP_SERVER_URL}/sales/dashboard/salespersons?${params}`
      );
      const data = response.data.data || [];
      
      commit('SET_CACHE_DATA', { dataType: 'salespersonStats', cacheKey, data });
      commit('SET_CURRENT_SALESPERSONS', data);
      
      return { fromCache: false, data };
    } catch (error) {
      console.error('Error fetching salesperson stats:', error);
      commit('SET_ERROR', error.message);
      throw error;
    }
  },
  
  /**
   * Fetch contracts with caching
   */
  async fetchContracts({ commit, state, getters }, { limit = 5000, force = false } = {}) {
    const cacheKey = generateCacheKey(state.filters, { limit });
    
    commit('SET_CONTRACTS_LOADING', true);
    
    if (!force && getters.isCacheFresh('contracts', cacheKey)) {
      const cachedData = state.cache.contracts[cacheKey].data;
      commit('SET_CURRENT_CONTRACTS', cachedData);
      commit('SET_CONTRACTS_LOADING', false);
      return { fromCache: true, data: cachedData };
    }
    
    try {
      const params = new URLSearchParams({
        limit,
        status: 'all', // Include all statuses to get churned contracts
        ...getters.filterParams
      });
      const response = await axios.get(
        `${process.env.VUE_APP_SERVER_URL}/sales/dashboard/contracts?${params}`
      );
      const data = response.data.data || [];
      
      commit('SET_CACHE_DATA', { dataType: 'contracts', cacheKey, data });
      commit('SET_CURRENT_CONTRACTS', data);
      
      return { fromCache: false, data };
    } catch (error) {
      console.error('Error fetching contracts:', error);
      commit('SET_ERROR', error.message);
      throw error;
    } finally {
      commit('SET_CONTRACTS_LOADING', false);
    }
  },
  
  /**
   * Fetch sellers (cached globally, doesn't depend on filters)
   */
  async fetchSellers({ commit, state }, { force = false } = {}) {
    // Check if sellers cache is fresh (5 minutes)
    if (!force && state.sellersTimestamp && (Date.now() - state.sellersTimestamp < CACHE_DURATION)) {
      return { fromCache: true, data: state.sellers };
    }
    
    try {
      const response = await axios.get(
        `${process.env.VUE_APP_SERVER_URL}/sales/dashboard/sellers`
      );
      const data = response.data.data || [];
      
      commit('SET_SELLERS', data);
      
      return { fromCache: false, data };
    } catch (error) {
      console.error('Error fetching sellers:', error);
      commit('SET_ERROR', error.message);
      throw error;
    }
  },
  
  /**
   * Fetch sales velocity with caching
   */
  async fetchVelocity({ commit, state, getters }, { force = false } = {}) {
    const cacheKey = getters.currentCacheKey;
    
    if (!force && getters.isCacheFresh('velocity', cacheKey)) {
      const cachedData = state.cache.velocity[cacheKey].data;
      commit('SET_CURRENT_VELOCITY', cachedData);
      return { fromCache: true, data: cachedData };
    }
    
    try {
      const response = await axios.get(
        `${process.env.VUE_APP_SERVER_URL}/sales/dashboard/velocity`
      );
      const data = response.data.data || null;
      
      commit('SET_CACHE_DATA', { dataType: 'velocity', cacheKey, data });
      commit('SET_CURRENT_VELOCITY', data);
      
      return { fromCache: false, data };
    } catch (error) {
      console.error('Error fetching velocity:', error);
      commit('SET_CURRENT_VELOCITY', null);
      return { fromCache: false, data: null };
    }
  },
  
  /**
   * Fetch products (cached globally)
   */
  async fetchProducts({ commit, state }, { force = false } = {}) {
    const cache = state.cache.products;
    
    if (!force && cache.timestamp && (Date.now() - cache.timestamp < CACHE_DURATION)) {
      commit('SET_CURRENT_PRODUCTS', cache.data);
      return { fromCache: true, data: cache.data };
    }
    
    try {
      const response = await axios.get(
        `${process.env.VUE_APP_SERVER_URL}/products`
      );
      const data = response.data.data || [];
      
      commit('SET_PRODUCTS_CACHE', { data, timestamp: Date.now() });
      commit('SET_CURRENT_PRODUCTS', data);
      
      return { fromCache: false, data };
    } catch (error) {
      console.error('Error fetching products:', error);
      commit('SET_CURRENT_PRODUCTS', []);
      return { fromCache: false, data: [] };
    }
  },
  
  /**
   * Fetch all dashboard data
   */
  async fetchAllDashboardData({ commit, dispatch }, { force = false, showLoading = false } = {}) {
    commit('SET_ERROR', null);
    
    // Show loading spinner if requested (initial load or force refresh)
    if (showLoading) {
      commit('SET_LOADING', true);
    }
    
    try {
      const results = await Promise.all([
        dispatch('fetchKPIs', { force }),
        dispatch('fetchMonthlyRevenue', { force }),
        dispatch('fetchProductStats', { force }),
        dispatch('fetchSalespersonStats', { force }),
        dispatch('fetchContracts', { force }),
        dispatch('fetchSellers', { force }),
        dispatch('fetchVelocity', { force }),
        dispatch('fetchProducts', { force })
      ]);
      
      const fromCacheCount = results.filter(r => r.fromCache).length;
      return { success: true, fromCacheCount };
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      commit('SET_ERROR', error.message);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  /**
   * Force refresh all data (bypasses cache)
   */
  async refreshAllData({ dispatch }) {
    return dispatch('fetchAllDashboardData', { force: true });
  },
  
  /**
   * Update filters and optionally refetch data
   */
  updateFilters({ commit }, filters) {
    commit('SET_FILTERS', filters);
  },
  
  /**
   * Reset filters
   */
  resetFilters({ commit }) {
    commit('RESET_FILTERS');
  },
  
  /**
   * Set selected year
   */
  setSelectedYear({ commit }, year) {
    commit('SET_SELECTED_YEAR', year);
  },
  
  /**
   * Clear all cache
   */
  clearCache({ commit }) {
    commit('CLEAR_ALL_CACHE');
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
